import { deriveUserAdded, deriveSeatState } from '#shared/utilities/schedule/user-added';

/**
 * The per-viewer state a session card needs, applied to a list of sessions.
 *
 * Three facts drive every card treatment — is it on MY schedule, do I hold a
 * seat or a waitlist place, and is it clashing with something else I have — and
 * none of them can come from the sessions feed. That response is cached and
 * shared between viewers, so it carries no per-user fields at all: `user_added`
 * and `user_has_seat` are only ever populated on the dev `?randomize` path,
 * which is why any surface reading them straight off the row sees `undefined`
 * for a real visitor and renders the plain variant forever.
 *
 * The agenda has always overlaid these client-side. Nothing else did, so the
 * same session showed as conflicting and needing a seat on My Schedule and as an
 * ordinary card in the Featured carousel, the Related rails, and the speaker
 * surfaces (Nick, Asana 1217708959827831 and the tickets it generalises).
 * Hoisted here so every surface applies one definition rather than restating it
 * and drifting.
 *
 * Returns a NEW array of new objects; the input rows are never mutated, which
 * matters because the catalog memo hands the same objects to every caller — one
 * viewer's state written onto a shared row would leak to the next. The copies
 * come from deriveUserAdded/deriveSeatState, which is why the conflict pass
 * below runs AFTER them and not before.
 */
export const useViewerSessionState = () => {
    const { user, loggedIn } = useUserSession();
    const { conflictIds, ensureConflictIds } = useScheduleConflicts();

    // Prime the shared conflict cache. Only the agenda, the session sidebar and
    // the My Event modal ever loaded it, so on the homepage or a speaker page
    // the set was simply empty and this overlay would have marked nothing —
    // looking exactly like the bug it is meant to fix. ensureConflictIds is
    // load-once per visit and a no-op when logged out, so every surface can ask.
    // Client-only: SSR has no viewer, and the endpoint is per-user.
    onMounted(() => { ensureConflictIds(); });

    // The sealed session can hydrate AFTER mount, and the call above is the only
    // one this surface makes. Asked while logged out it comes back with nothing
    // — which loadConflictIds no longer caches, but nothing would retry either,
    // so every card on the page would read as unclashing for the rest of the
    // visit. Watching the flip is what turns that into a retry.
    watch(loggedIn, (isLoggedIn) => { if (isLoggedIn) ensureConflictIds(); });

    const applyViewerState = (rows) => {
        if (!rows?.length) return rows || [];

        const withSchedule = deriveSeatState(
            deriveUserAdded(rows, user.value?.scheduled || []),
            user.value,
        );

        // Marked by unique_id, the same key the conflicts endpoint reports and
        // the agenda's own overlay uses. Skipped entirely when nothing clashes,
        // so the common case allocates nothing extra.
        const ids = conflictIds.value;
        if (!ids?.size) return withSchedule;
        return withSchedule.map((s) => (
            ids.has(s.unique_id) ? { ...s, is_conflicted: true } : s
        ));
    };

    /**
     * What the card's `action-required` prop means, in one place: on my
     * schedule, needs a seat, and I hold neither a seat nor a waitlist place.
     * Every consumer had this inline, which is how they drifted.
     */
    const needsSeat = (session) => (
        !!session?.user_added
        && session?.session_reservations === true
        && !session?.user_has_seat
        && !session?.user_waitlisted
    );

    return { applyViewerState, needsSeat };
};
